import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, X } from "lucide-react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary capturou uma exceção:", error, errorInfo);
  }

  public handleClose = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  public handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            background: "rgba(11, 15, 25, 0.85)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={this.handleClose}
        >
          <div
            style={{
              background: "#0f172a",
              border: "1px solid rgba(239, 68, 68, 0.4)",
              borderRadius: "20px",
              padding: "32px 28px",
              maxWidth: "500px",
              width: "100%",
              textAlign: "center",
              boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.9), 0 0 30px rgba(239, 68, 68, 0.2)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botão de Fechar no topo */}
            <button
              onClick={this.handleClose}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "transparent",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                padding: "6px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Fechar"
            >
              <X size={20} />
            </button>

            {/* Ícone de Alerta */}
            <div
              style={{
                background: "rgba(239, 68, 68, 0.15)",
                color: "#f87171",
                padding: "16px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(239, 68, 68, 0.3)",
              }}
            >
              <AlertTriangle size={36} />
            </div>

            {/* Título */}
            <h2 style={{ fontSize: "1.25rem", color: "#f8fafc", fontWeight: 700, margin: 0 }}>
              {this.props.fallbackTitle || "Ocorreu uma instabilidade na exibição"}
            </h2>

            {/* Mensagem descritiva */}
            <p style={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.5, margin: 0, maxWidth: "420px" }}>
              {this.state.error?.message || "Não foi possível carregar as informações deste componente no momento."}
            </p>

            {/* Ações */}
            <div style={{ display: "flex", gap: "10px", marginTop: "8px", flexWrap: "wrap", justifyContent: "center" }}>
              {this.props.onReset && (
                <button
                  onClick={this.handleClose}
                  style={{
                    background: "rgba(255, 255, 255, 0.08)",
                    color: "#f8fafc",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    borderRadius: "10px",
                    padding: "10px 18px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Fechar Janela
                </button>
              )}

              <button
                onClick={this.handleReload}
                style={{
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 20px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 14px rgba(59, 130, 246, 0.4)",
                }}
              >
                <RefreshCw size={15} /> Recarregar Aplicação
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
